import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { debounce } from 'lodash';

function MatrizFilialList() {
  const navigate = useNavigate();

  // Filtros
  const [descricao, setDescricao] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [status, setStatus] = useState(true);

  // Lista e Paginação
  const [matrizes, setMatrizes] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Config
  const registrosPorPagina = 10;
  const API_URL = '/api/ComMatrizFiliais';

  // Função para chamar o back-end
  const buscarMatrizes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          descricao,
          cnpj,
          status,
          page: pagina - 1, // zero-based
          size: registrosPorPagina
        }
      });
      setMatrizes(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar Matriz/Filiais:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas em excesso
  const debouncedBuscar = useCallback(
    debounce(() => {
      buscarMatrizes();
    }, 500),
    [descricao, cnpj, status, pagina]
  );

  // useEffect para disparar busca automática
  useEffect(() => {
    debouncedBuscar();
    return debouncedBuscar.cancel;
  }, [descricao, cnpj, status, pagina, debouncedBuscar]);

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setDescricao('');
    setCnpj('');
    setStatus(true);
    setPagina(1);
  };

  // Ações
  const handleNovo = () => {
    navigate('/matrizFilial/edit'); // Ajuste conforme rota
  };

  const handleEditar = (id) => {
    navigate(`/matrizFilial/edit/${id}`);
  };

  const handleExcluir = async (id) => {
    console.log('Excluindo ID:', id);
    // Exemplo:
    // await axios.delete(`${API_URL}/${id}`);
    // buscarMatrizes();
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop Carregamento */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título */}
      <Typography variant="h4" align="center" gutterBottom>
        Listagem de Matriz/Filial
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Descrição"
                variant="outlined"
                size="small"
                fullWidth
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CNPJ"
                variant="outlined"
                size="small"
                fullWidth
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                  />
                }
                label="Ativo?"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button variant="outlined" onClick={limparFiltros} fullWidth>
                Limpar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botão "+ Novo" */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleNovo}>
          + Novo
        </Button>
      </Box>

      {/* Tabela de Resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Descrição</strong></TableCell>
              <TableCell><strong>CNPJ</strong></TableCell>
              <TableCell><strong>Cidade</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matrizes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhuma Matriz/Filial encontrada.
                </TableCell>
              </TableRow>
            ) : (
              matrizes.map((m, index) => (
                <TableRow key={index}>
                  <TableCell>{m.id}</TableCell>
                  <TableCell>{m.descricao}</TableCell>
                  <TableCell>{m.cnpj}</TableCell>
                  <TableCell>{m.cidade}</TableCell>
                  <TableCell>{m.status ? 'Ativo' : 'Inativo'}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleEditar(m.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleExcluir(m.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default MatrizFilialList;
